// Production Task-selection step — Step 2 of the Evaluation Stepper (Screen 05, FR17/FR18,
// Story 6.4). Ported from the throwaway `evaluation-mockup/TaskSelectionStep.tsx` (which
// encoded the exact Screen-05 layout): the markup/interactions are kept; the DATA is swapped
// from faked `mock-data` to the real `useAmpsTasks` catalogue, and the LabeledSelect to the
// shared `Select` + `FormField` primitives.
//
// PRESENTATIONAL over lifted state (the seam 6.3's stepper drops into): the step owns the
// catalogue fetch + the task GRID, but NOT evaluationId / version / persistence. The stepper
// owns the left rail, topbar, footer (Back / Save draft / Continue) and the save itself (via
// the shared `useSaveEvaluationDraft`) — that is what keeps the save-the-WHOLE-draft contract
// (AC 7) correct (the step never composes a `{tasks}`-only body).
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import {
  distinctCategories,
  tasksInCategory,
  useAmpsTasks,
} from "@/hooks/queries/useAmpsTasks";
import type { SelectedTask } from "@/hooks/queries/evaluation-draft-types";
import type { AmpsTask } from "@/hooks/queries/amps-task-types";
import { cn } from "@/lib/utils";

export const MIN_TASKS = 2;
export const MAX_TASKS = 4;

export type TaskSelectionStepProps = {
  /** Lifted draft state: one entry per task card. May be PARTIAL (category, no code). */
  tasks: SelectedTask[];
  /** Push the next card array up into the parent stepper draft state. */
  onTasksChange: (next: SelectedTask[]) => void;
};

const EMPTY_CARD: SelectedTask = { task_category: "", task_code: "" };

/** Pad the lifted state up to the 2-task floor so Task 1 + Task 2 are always visible (AC 2). */
function padToFloor(tasks: SelectedTask[]): SelectedTask[] {
  if (tasks.length >= MIN_TASKS) return tasks;
  return [
    ...tasks,
    ...Array.from({ length: MIN_TASKS - tasks.length }, () => ({ ...EMPTY_CARD })),
  ];
}

/** The fully-selected cards (non-empty `task_code`) — exactly what persists to the draft (AC 6). */
export function persistableTasks(tasks: SelectedTask[]): SelectedTask[] {
  return tasks.filter((task) => task.task_code !== "");
}

/** Task codes that appear in more than one card (non-empty only) — the duplicate set (AC 5). */
export function duplicateTaskCodes(tasks: SelectedTask[]): string[] {
  const counts = new Map<string, number>();
  for (const task of tasks) {
    if (!task.task_code) continue;
    counts.set(task.task_code, (counts.get(task.task_code) ?? 0) + 1);
  }
  return [...counts.entries()].filter(([, n]) => n > 1).map(([code]) => code);
}

export function TaskSelectionStep({ tasks, onTasksChange }: TaskSelectionStepProps) {
  // The step is self-sufficient: it fetches its own catalogue so the loading / error host
  // (AC 1) lives here. A stepper that prefers to own the fetch can lift this later.
  const { data: catalogue, isPending, isError, refetch } = useAmpsTasks();

  const cards = padToFloor(tasks);
  const duplicates = duplicateTaskCodes(cards);
  const categories = catalogue ? distinctCategories(catalogue) : [];

  function updateCard(index: number, patch: Partial<SelectedTask>) {
    onTasksChange(cards.map((card, i) => (i === index ? { ...card, ...patch } : card)));
  }

  // Changing the category resets the card's specific task (AC 3) — the prior code is no
  // longer valid under the new category.
  function setCategory(index: number, category: string) {
    updateCard(index, { task_category: category, task_code: "" });
  }

  function setSpecific(index: number, code: string) {
    updateCard(index, { task_code: code });
  }

  function addTask() {
    if (cards.length >= MAX_TASKS) return;
    onTasksChange([...cards, { ...EMPTY_CARD }]);
  }

  // Remove never drops below the 2-task floor — only cards at index ≥ MIN_TASKS are removable.
  function removeTask(index: number) {
    if (cards.length <= MIN_TASKS || index < MIN_TASKS) return;
    onTasksChange(cards.filter((_, i) => i !== index));
  }

  return (
    <div className="grid gap-[32px]">
      <header>
        <h1 className="display-xs text-[color:var(--text-primary)]">Observed task selection</h1>
        <p className="mt-1 text-md text-[color:var(--text-tertiary)]">
          Choose the AMPS tasks the client completed during the evaluation. Task 1 and Task 2 are
          required; add Task 3 and Task 4 as needed.
        </p>
      </header>

      {/* AC 1 — catalogue loading / error host: never render empty selects silently. */}
      {isError ? (
        <Banner variant="danger" title="Couldn't load the AMPS task list." role="alert">
          <div className="flex flex-col gap-2">
            <p>Check your connection and try again — your selections are not lost.</p>
            <div>
              <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          </div>
        </Banner>
      ) : isPending ? (
        <Banner variant="info" title="Loading the AMPS task list…" role="status">
          <p>One moment while the task catalogue loads.</p>
        </Banner>
      ) : !catalogue || catalogue.length === 0 ? (
        // AC 1 — a 200 with an EMPTY catalogue (mis-seeded / half-rotated reference
        // data) must not silently render category selects with no options. Surface it
        // explicitly with a retry, same as the error host.
        <Banner variant="warn" title="No AMPS tasks are available." role="status">
          <div className="flex flex-col gap-2">
            <p>The task catalogue came back empty, so there are no tasks to choose yet.</p>
            <div>
              <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          </div>
        </Banner>
      ) : (
        <>
          {duplicates.length > 0 ? (
            <Banner variant="warn" title="The same task is selected more than once.">
              <p>
                {duplicates.join(", ")} appears in more than one slot. Pick a different task so each
                slot is unique.
              </p>
            </Banner>
          ) : null}

          {/* Index keys are safe here (TaskCard is fully controlled, no internal state).
              A stable per-card `_id` belongs in the state owner — Story 6.3's stepper, which
              holds the lifted draft — so it can be minted on create/resume and stripped at the
              single save seam (the wire `SelectedTask` is `extra="forbid"`); not in this
              presentational step, where immutable updates recreate card objects each change. */}
          <div className="grid gap-[16px] md:grid-cols-2">
            {cards.map((card, index) => (
              <TaskCard
                key={index}
                index={index}
                card={card}
                catalogue={catalogue}
                categories={categories}
                removable={index >= MIN_TASKS}
                isDuplicate={Boolean(card.task_code) && duplicates.includes(card.task_code)}
                onCategoryChange={(value) => setCategory(index, value)}
                onSpecificChange={(value) => setSpecific(index, value)}
                onRemove={() => removeTask(index)}
              />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={addTask}
              disabled={cards.length >= MAX_TASKS}
            >
              Add Task
            </Button>
            <span className="text-sm text-[color:var(--text-tertiary)]">
              {cards.length >= MAX_TASKS
                ? "Maximum of 4 tasks reached."
                : `Add Task reveals Task ${cards.length + 1}${
                    cards.length + 1 < MAX_TASKS ? `, then Task ${MAX_TASKS}` : ""
                  }.`}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

type TaskCardProps = {
  index: number;
  card: SelectedTask;
  catalogue: AmpsTask[];
  categories: string[];
  removable: boolean;
  isDuplicate: boolean;
  onCategoryChange: (value: string) => void;
  onSpecificChange: (value: string) => void;
  onRemove: () => void;
};

function TaskCard({
  index,
  card,
  catalogue,
  categories,
  removable,
  isDuplicate,
  onCategoryChange,
  onSpecificChange,
  onRemove,
}: TaskCardProps) {
  const taskNumber = index + 1;
  const specificOptions = tasksInCategory(catalogue, card.task_category);

  return (
    <section
      aria-label={`Task ${taskNumber}`}
      className={cn(
        "grid min-w-0 gap-[16px] rounded-2xl border p-[20px]",
        isDuplicate
          ? "border-[color:var(--border-error)] bg-[color:var(--bg-error-primary)]"
          : "border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)]",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
          Task {taskNumber}
          {card.task_code ? (
            <span className="ml-2 rounded-sm bg-[color:var(--bg-tertiary)] px-2 py-0.5 text-sm font-semibold tabular-nums text-[color:var(--text-secondary)]">
              {card.task_code}
            </span>
          ) : null}
        </h3>
        {removable ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            aria-label={`Remove Task ${taskNumber}`}
          >
            Remove
          </Button>
        ) : null}
      </div>

      <FormField label="Task category">
        <SearchableCombobox
          options={categories.map((c) => ({ value: c, label: c }))}
          value={card.task_category}
          onChange={onCategoryChange}
          placeholder="Select category…"
          searchAriaLabel="Task category"
          showSearch={false}
        />
      </FormField>

      <FormField
        label="Specific task"
        error={isDuplicate ? "This task is already selected in another slot." : undefined}
      >
        <SearchableCombobox
          options={specificOptions.map((t) => ({ value: t.code, label: `${t.code} — ${t.name}` }))}
          value={card.task_code}
          onChange={onSpecificChange}
          placeholder={card.task_category ? "Select task…" : "Choose a category first"}
          searchAriaLabel="Specific task"
          disabled={!card.task_category}
        />
      </FormField>
    </section>
  );
}
